import React, { useState, useRef, useEffect } from "react";
import "rrweb-player/dist/style.css";
import "rrweb-player";
import { searchErrorMonitoringLog, updateErrorStatus } from "../../services/api/errorMonitoring";
import { useDispatch } from "react-redux";
import { raiseToast } from "../../services/redux/reducers/toast/toast";
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { MultiSelect } from 'primereact/multiselect';

const ErrorDashboard = () => {
    const [events, setEvents] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sessionList, setSessionList] = useState([]);
    const [selectedSession, setSelectedSession] = useState(null);
    const [loadingSessions, setLoadingSessions] = useState(true);
    const [showStackTrace, setShowStackTrace] = useState(false);
    const [pageNo, setPageNo] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [filters, setFilters] = useState({
        searchText: '',
        statusIds: [],
        env: null,
        handled: null,
        startDate: null,
        endDate: null
    });
    const playerRef = useRef(null);
    const sessionListRef = useRef(null);
    const dispatch = useDispatch();

    const statusOptions = [
        { label: 'Open', value: 1 },
        { label: 'Resolved', value: 2 }
    ];

    const envOptions = [
        { label: 'Development', value: 'development' },
        { label: 'Production', value: 'production' }
    ];

    const handledOptions = [
        { label: 'Handled', value: true },
        { label: 'Unhandled', value: false }
    ];

    useEffect(() => {
        fetchSessions(true);
    }, [filters]);

    const fetchSessions = async (reset = false) => {
        if (reset) {
            setPageNo(1);
            setSessionList([]);
        }
        
        setLoadingSessions(true);
        const response = await searchErrorMonitoringLog({
            recordsPerPage: 20,
            pageNo: reset ? 1 : pageNo,
            ...filters,
            startDate: filters.startDate ? filters.startDate.toISOString() : null,
            endDate: filters.endDate ? filters.endDate.toISOString() : null
        });
        
        if (response.failure) {
            dispatch(raiseToast({ severity: 'error', detail: response.data.error, life: 3000 }));
        } else if (response.data) {
            const newSessions = response.data;
            setSessionList(prev => reset ? newSessions : [...prev, ...newSessions]);
            setHasMore(newSessions.length === 20);
        }
        setLoadingSessions(false);
    };

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollHeight - scrollTop <= clientHeight * 1.5 && !loadingSessions && hasMore) {
            setPageNo(prev => prev + 1);
            fetchSessions();
        }
    };

    useEffect(() => {
        const mountPlayer = async () => {
            if (events && playerRef.current) {
                playerRef.current.innerHTML = ""; // clear previous player

                const { default: RRWebPlayer } = await import("rrweb-player");
                new RRWebPlayer({
                    target: playerRef.current,
                    props: { events },
                });
            }
        };

        mountPlayer();
    }, [events]);

    const loadSession = async (session) => {
        setLoading(true);
        try {
            const res = await fetch(session.replayUrl);
            if (!res.ok) {
                throw new Error(`Failed to load session replay: ${res.statusText}`);
            }
            const data = await res.json();
            setEvents(data);
            setSelectedSession(session);
        } catch (error) {
            dispatch(raiseToast({ 
                severity: 'error', 
                detail: error.message || 'Failed to load session replay', 
                life: 3000 
            }));
        } finally {
            setLoading(false);
        }
    };

    const handleStatusUpdate = async (logId, newStatusId) => {
        try {
            const response = await updateErrorStatus(logId, newStatusId);
            if (response.failure) {
                dispatch(raiseToast({ severity: 'error', detail: response.data.error, life: 3000 }));
            } else {
                dispatch(raiseToast({ severity: 'success', detail: 'Status updated successfully', life: 3000 }));
                fetchSessions(true); // Refresh the list
            }
        } catch (error) {
            dispatch(raiseToast({ 
                severity: 'error', 
                detail: error.message || 'Failed to update status', 
                life: 3000 
            }));
        }
    };

    const confirmStatusUpdate = (logId, newStatusId) => {
        confirmDialog({
            message: 'Are you sure you want to mark this error as resolved?',
            header: 'Confirmation',
            icon: 'pi pi-exclamation-triangle',
            accept: () => handleStatusUpdate(logId, newStatusId)
        });
    };

    const getStatusBadgeClass = (statusId) => {
        switch (statusId) {
            case 1:
                return 'bg-yellow-100 text-yellow-900';
            case 2:
                return 'bg-green-100 text-green-900';
            default:
                return 'bg-gray-100 text-gray-900';
        }
    };

    const getStatusText = (statusId) => {
        switch (statusId) {
            case 1:
                return 'Open';
            case 2:
                return 'Resolved';
            default:
                return 'Unknown';
        }
    };

    return (
        <div className="p-3">
            <ConfirmDialog />
            <h1 className="text-xl font-bold mb-3">Error Monitoring Dashboard</h1>
            
            {/* Filters */}
            <div className="grid mb-3">
                <div className="col-12 md:col-3">
                    <span className="p-input-icon-left w-full">
                        <i className="pi pi-search" />
                        <InputText
                            value={filters.searchText}
                            onChange={(e) => setFilters(prev => ({ ...prev, searchText: e.target.value }))}
                            placeholder="Search by error message, URL, or user agent"
                            className="w-full"
                        />
                    </span>
                </div>
                <div className="col-12 md:col-2">
                    <MultiSelect
                        value={filters.statusIds}
                        options={statusOptions}
                        onChange={(e) => setFilters(prev => ({ ...prev, statusIds: e.value }))}
                        placeholder="Status"
                        className="w-full"
                    />
                </div>
                <div className="col-12 md:col-2">
                    <Dropdown
                        value={filters.env}
                        options={envOptions}
                        onChange={(e) => setFilters(prev => ({ ...prev, env: e.value }))}
                        placeholder="Environment"
                        className="w-full"
                    />
                </div>
                <div className="col-12 md:col-2">
                    <Dropdown
                        value={filters.handled}
                        options={handledOptions}
                        onChange={(e) => setFilters(prev => ({ ...prev, handled: e.value }))}
                        placeholder="Error Type"
                        className="w-full"
                    />
                </div>
                <div className="col-12 md:col-3">
                    <div className="flex gap-2">
                        <Calendar
                            value={filters.startDate}
                            onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.value }))}
                            placeholder="Start Date"
                            showIcon
                            className="w-full"
                        />
                        <Calendar
                            value={filters.endDate}
                            onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.value }))}
                            placeholder="End Date"
                            showIcon
                            className="w-full"
                        />
                    </div>
                </div>
            </div>

            <div className="flex gap-3">
                {/* Left side - Session List */}
                <div className="w-1/4">
                    <div 
                        ref={sessionListRef}
                        className="border-round surface-card p-2 h-full overflow-y-auto" 
                        style={{ maxHeight: 'calc(100vh - 120px)' }}
                        onScroll={handleScroll}
                    >
                        {loadingSessions && sessionList.length === 0 ? (
                            <div className="flex align-items-center justify-content-center p-4">
                                <i className="pi pi-spin pi-spinner" style={{ fontSize: '1.5rem' }}></i>
                            </div>
                        ) : sessionList.length === 0 ? (
                            <div className="text-center p-4 text-sm">No error logs found</div>
                        ) : (
                            sessionList.map((session) => (
                                <div
                                    key={session.logId}
                                    className={`border-round p-2 mb-2 cursor-pointer transition-colors transition-duration-150 ${
                                        selectedSession?.logId === session.logId 
                                        ? 'bg-blue-100 text-blue-900' 
                                        : 'surface-hover'
                                    }`}
                                    onClick={() => loadSession(session)}
                                >
                                    <div className="flex justify-content-between align-items-center mb-1">
                                        <h2 className="font-semibold text-sm m-0">
                                            {session.firstName && session.lastName 
                                                ? `${session.firstName} ${session.lastName}`
                                                : 'Anonymous User'}
                                        </h2>
                                        <div className="flex gap-1 align-items-center">
                                            <span className={`text-xs px-1 py-0 border-round ${getStatusBadgeClass(session.statusId)}`}>
                                                {getStatusText(session.statusId)}
                                            </span>
                                            {session.statusId === 1 && (
                                                <Button
                                                    icon="pi pi-check"
                                                    className="p-button-rounded p-button-success p-button-sm"
                                                    style={{ width: '1.5rem', height: '1.5rem' }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        confirmStatusUpdate(session.logId, 2);
                                                    }}
                                                    tooltip="Mark as Resolved"
                                                />
                                            )}
                                        </div>
                                    </div>
                                    <p className="text-xs text-500 m-0 mb-1">
                                        {new Date(session.createdAt).toLocaleString()}
                                    </p>
                                    <p className="text-xs text-500 m-0 mb-1 line-clamp-2">
                                        {session.errorMessage}
                                    </p>
                                    <div className="flex gap-2">
                                        <span className="text-xs text-500">Env: {session.env}</span>
                                        <span className="text-xs text-500">Browser: {session.userAgent}</span>
                                    </div>
                                </div>
                            ))
                        )}
                        {loadingSessions && sessionList.length > 0 && (
                            <div className="flex align-items-center justify-content-center p-2">
                                <i className="pi pi-spin pi-spinner" style={{ fontSize: '1rem' }}></i>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right side - Player */}
                <div className="w-3/4">
                    {loading && (
                        <div className="flex align-items-center justify-content-center p-4">
                            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
                        </div>
                    )}

                    {events && !loading && (
                        <div className="border-round surface-card p-2">
                            <div className="flex justify-content-between align-items-center mb-2">
                                <h2 className="text-lg font-semibold m-0">
                                    Replaying: {selectedSession.firstName && selectedSession.lastName 
                                        ? `${selectedSession.firstName} ${selectedSession.lastName}`
                                        : 'Anonymous User'}
                                </h2>
                                <div className="flex gap-2">
                                    <Button 
                                        label="View Stack Trace" 
                                        icon="pi pi-code" 
                                        className="p-button-outlined p-button-sm"
                                        style={{ fontSize: '0.875rem' }}
                                        onClick={() => setShowStackTrace(true)}
                                    />
                                    <Button 
                                        label="View Page" 
                                        icon="pi pi-external-link" 
                                        className="p-button-outlined p-button-sm"
                                        style={{ fontSize: '0.875rem' }}
                                        onClick={() => window.open(selectedSession.pageUrl, '_blank')}
                                    />
                                </div>
                            </div>
                            {events && events.length >= 2 ? (
                                <div ref={playerRef}></div>
                            ) : (
                                <div className="p-4 text-center text-500">
                                    No replay data available for this error
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Stack Trace Dialog */}
            <Dialog 
                header="Error Details" 
                visible={showStackTrace} 
                style={{ width: '80vw' }} 
                onHide={() => setShowStackTrace(false)}
                maximizable
            >
                <div className="p-3">
                    <div className="mb-4">
                        <h3 className="text-lg font-semibold mb-2">Error Message:</h3>
                        <div className="bg-red-50 border-round p-3">
                            <p className="text-red-500 m-0 font-medium">{selectedSession?.errorMessage}</p>
                        </div>
                    </div>

                    <div className="mb-4">
                        <h3 className="text-lg font-semibold mb-2">Environment:</h3>
                        <div className="bg-gray-50 border-round p-3">
                            <div className="grid">
                                <div className="col-12 md:col-6">
                                    <p className="m-0"><span className="font-medium">Environment:</span> {selectedSession?.env}</p>
                                </div>
                                <div className="col-12 md:col-6">
                                    <p className="m-0"><span className="font-medium">Browser:</span> {selectedSession?.userAgent}</p>
                                </div>
                                <div className="col-12">
                                    <p className="m-0"><span className="font-medium">Page URL:</span> {selectedSession?.pageUrl}</p>
                                </div>
                                <div className="col-12">
                                    <p className="m-0"><span className="font-medium">Time:</span> {new Date(selectedSession?.createdAt).toLocaleString()}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div>
                        <h3 className="text-lg font-semibold mb-2">Stack Trace:</h3>
                        <div className="bg-gray-50 border-round p-3">
                            <pre className="m-0 overflow-auto" style={{ 
                                maxHeight: '60vh',
                                whiteSpace: 'pre-wrap',
                                wordWrap: 'break-word',
                                fontFamily: 'monospace',
                                fontSize: '0.875rem',
                                lineHeight: '1.5'
                            }}>
                                {selectedSession?.stackTrace?.split('\n').map((line, index) => (
                                    <div key={index} className="mb-1">
                                        {line.trim()}
                                    </div>
                                ))}
                            </pre>
                        </div>
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default ErrorDashboard;
